import {
    createEmptyItem,
    createEmptyMeal,
    createEmptyOption,
    syncDaysWithDuration
} from './nutritionPlanModel';

const updateDay = (state, dayId, updater) => ({
    ...state,
    days: state.days.map((day) => (day._uiId === dayId ? updater(day) : day))
});

const updateMeal = (state, dayId, mealId, updater) =>
    updateDay(state, dayId, (day) => ({
        ...day,
        meals: day.meals.map((meal) => (meal._uiId === mealId ? updater(meal) : meal))
    }));

const updateOption = (state, dayId, mealId, optionId, updater) =>
    updateMeal(state, dayId, mealId, (meal) => ({
        ...meal,
        options: meal.options.map((option) =>
            option._uiId === optionId ? updater(option) : option
        )
    }));

const renumberOptions = (options) =>
    options.map((option, index) => ({ ...option, label: `Opção ${index + 1}` }));

export const nutritionPlanReducer = (state, action) => {
    switch (action.type) {
        case 'setField':
            return { ...state, [action.field]: action.value };
        case 'syncDays':
            return {
                ...state,
                days: syncDaysWithDuration(state.days, state.durationDays)
            };
        case 'updateDay':
            return updateDay(state, action.dayId, (day) => ({
                ...day,
                [action.field]: action.value
            }));
        case 'addMeal':
            return updateDay(state, action.dayId, (day) => ({
                ...day,
                meals: [...day.meals, createEmptyMeal()]
            }));
        case 'deleteMeal':
            return updateDay(state, action.dayId, (day) => ({
                ...day,
                meals: day.meals.filter((meal) => meal._uiId !== action.mealId)
            }));
        case 'duplicateMeal':
            return updateDay(state, action.dayId, (day) => {
                const source = day.meals.find((meal) => meal._uiId === action.mealId);
                if (!source) return day;
                const duplicate = createEmptyMeal();
                duplicate.mealType = source.mealType;
                duplicate.title = source.title;
                duplicate.options = source.options.map((option, index) => ({
                    ...createEmptyOption(`Opção ${index + 1}`),
                    items: option.items.map((item) => ({
                        ...createEmptyItem(),
                        name: item.name,
                        quantity: item.quantity
                    }))
                }));
                return { ...day, meals: [...day.meals, duplicate] };
            });
        case 'updateMeal':
            return updateMeal(state, action.dayId, action.mealId, (meal) => ({
                ...meal,
                [action.field]: action.value
            }));
        case 'addOption':
            return updateMeal(state, action.dayId, action.mealId, (meal) => ({
                ...meal,
                options: [
                    ...meal.options,
                    createEmptyOption(`Opção ${meal.options.length + 1}`)
                ]
            }));
        case 'deleteOption':
            return updateMeal(state, action.dayId, action.mealId, (meal) => ({
                ...meal,
                options: renumberOptions(
                    meal.options.filter((option) => option._uiId !== action.optionId)
                )
            }));
        case 'updateOption':
            return updateOption(
                state,
                action.dayId,
                action.mealId,
                action.optionId,
                (option) => ({ ...option, [action.field]: action.value })
            );
        case 'addItem':
            return updateOption(
                state,
                action.dayId,
                action.mealId,
                action.optionId,
                (option) => ({
                    ...option,
                    items: [...option.items, createEmptyItem()]
                })
            );
        case 'deleteItem':
            return updateOption(
                state,
                action.dayId,
                action.mealId,
                action.optionId,
                (option) => ({
                    ...option,
                    items: option.items.filter((item) => item._uiId !== action.itemId)
                })
            );
        case 'updateItem':
            return updateOption(
                state,
                action.dayId,
                action.mealId,
                action.optionId,
                (option) => ({
                    ...option,
                    items: option.items.map((item) =>
                        item._uiId === action.itemId
                            ? { ...item, [action.field]: action.value }
                            : item
                    )
                })
            );
        case 'copyPreviousDay': {
            const target = state.days.find((day) => day._uiId === action.dayId);
            const source = state.days.find(
                (day) => Number(day.dayNumber) === Number(target?.dayNumber) - 1
            );
            if (!target || !source) return state;
            return updateDay(state, action.dayId, (day) => ({
                ...day,
                objective: source.objective,
                meals: source.meals.map((meal) => {
                    const copy = createEmptyMeal();
                    copy.mealType = meal.mealType;
                    copy.title = meal.title;
                    copy.options = meal.options.map((option, index) => ({
                        ...createEmptyOption(`Opção ${index + 1}`),
                        items: option.items.map((item) => ({
                            ...createEmptyItem(),
                            name: item.name,
                            quantity: item.quantity
                        }))
                    }));
                    return copy;
                })
            }));
        }
        case 'addRestriction':
            return {
                ...state,
                foodsToAvoid: [
                    ...state.foodsToAvoid,
                    { _uiId: action.id, category: '', items: [] }
                ]
            };
        case 'deleteRestriction':
            return {
                ...state,
                foodsToAvoid: state.foodsToAvoid.filter(
                    (group) => group._uiId !== action.groupId
                )
            };
        case 'updateRestriction':
            return {
                ...state,
                foodsToAvoid: state.foodsToAvoid.map((group) =>
                    group._uiId === action.groupId
                        ? { ...group, category: action.value }
                        : group
                )
            };
        case 'addRestrictionItem':
            return {
                ...state,
                foodsToAvoid: state.foodsToAvoid.map((group) =>
                    group._uiId === action.groupId
                        ? {
                              ...group,
                              items: [...group.items, { _uiId: action.id, value: '' }]
                          }
                        : group
                )
            };
        case 'updateRestrictionItem':
            return {
                ...state,
                foodsToAvoid: state.foodsToAvoid.map((group) =>
                    group._uiId === action.groupId
                        ? {
                              ...group,
                              items: group.items.map((item) =>
                                  item._uiId === action.itemId
                                      ? { ...item, value: action.value }
                                      : item
                              )
                          }
                        : group
                )
            };
        case 'deleteRestrictionItem':
            return {
                ...state,
                foodsToAvoid: state.foodsToAvoid.map((group) =>
                    group._uiId === action.groupId
                        ? {
                              ...group,
                              items: group.items.filter(
                                  (item) => item._uiId !== action.itemId
                              )
                          }
                        : group
                )
            };
        case 'addNote':
            return {
                ...state,
                customNotes: [...state.customNotes, { _uiId: action.id, value: '' }]
            };
        case 'updateNote':
            return {
                ...state,
                customNotes: state.customNotes.map((note) =>
                    note._uiId === action.noteId ? { ...note, value: action.value } : note
                )
            };
        case 'deleteNote':
            return {
                ...state,
                customNotes: state.customNotes.filter(
                    (note) => note._uiId !== action.noteId
                )
            };
        default:
            return state;
    }
};
